import PropTypes from 'prop-types'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { object, string } from 'yup'

import Button from '@/components/Button'
import Form, { Select, Text, Textarea } from '@/components/Form'
import {
  AZURE,
  IDP_NAME,
  OKTA,
  OTHER,
  setFieldValuesForIdentityProvider,
  shouldAutoFillOtherFields,
  validateXML
} from '@/pages/OrganizationSecurityPage/utils'

import messages from './messages'

function CreateSamlIdentityProvider(props: any) {
  const intl = useIntl()

  const [state, _setState] = useState({
    disabled: false,
    activeField: ''
  })
  const setState = (update: Partial<Parameters<typeof _setState>[0]>) => {
    return _setState(state => ({ ...state, ...(typeof update === 'function' ? update(state) : update) }))
  }

  const schema = object().shape({
    idpName: string()
      .trim()
      // @ts-expect-error
      .required(<FormattedMessage {...messages.requiredError} />),
    metadata: string()
      .trim()
      // @ts-expect-error
      .required(<FormattedMessage {...messages.requiredError} />)
      // @ts-expect-error
      .test('invalidXML', <FormattedMessage {...messages.metadataInvalidXml} />, validateXML),
    emailAttribute: string()
      .trim()
      // @ts-expect-error
      .required(<FormattedMessage {...messages.requiredError} />),
    usernameAttribute: string()
      .trim()
      // @ts-expect-error
      .required(<FormattedMessage {...messages.requiredError} />),
    familyNameAttribute: string()
      .trim()
      // @ts-expect-error
      .required(<FormattedMessage {...messages.requiredError} />)
  })

  const onCreate = (values: any) => {
    props.onCreate(values)
  }

  const setFieldsForProvider = (values: any) => {
    // When an identity provider is selected, update the other fields accordingly
    if (state.activeField === IDP_NAME) {
      setFieldValuesForIdentityProvider(values)
      setState({ disabled: shouldAutoFillOtherFields(values[IDP_NAME]) })
    }
  }

  // Not entirely sure if this is the right way to go for tracking the active field
  const setActiveField = (e: any) => {
    setState({ activeField: e.target.name })
  }

  return (
    <Form
      buttons={
        <Button color="primary" type="submit">
          {intl.formatMessage(messages.createButton)}
        </Button>
      }
      initialValues={{
        idpName: '',
        metadata: '',
        emailAttribute: '',
        usernameAttribute: '',
        familyNameAttribute: ''
      }}
      isActionInProgress={props.isActionInProgress}
      onSubmit={onCreate}
      onChange={setFieldsForProvider}
      schema={schema}
      enableReinitialize={true}
      title={<FormattedMessage {...messages.cardTitle} />}
    >
      <p>
        <FormattedMessage {...messages.description} />
      </p>
      <Select
        label={<FormattedMessage {...messages.identityProvider} />}
        name="idpName"
        required
        defaultValue={OTHER}
        onChange={setActiveField}
      >
        {/* @ts-expect-error */}
        <Select.Option value=""></Select.Option>
        <Select.Option value={AZURE}>{intl.formatMessage(messages.identityProviderAzureActiveDirectory)}</Select.Option>
        <Select.Option value={OKTA}>{intl.formatMessage(messages.identityProviderOkta)}</Select.Option>
        <Select.Option value={OTHER}>{intl.formatMessage(messages.identityProviderOther)}</Select.Option>
      </Select>
      <Textarea
        description={<FormattedMessage {...messages.metadataDescription} />}
        label={<FormattedMessage {...messages.metadata} />}
        name="metadata"
        placeholder={intl.formatMessage(messages.metadataPlaceholder)}
        required
        rows={15}
      />
      <Text
        label={<FormattedMessage {...messages.emailAttribute} />}
        name="emailAttribute"
        placeholder={intl.formatMessage(messages.emailAttributePlaceholder)}
        required
        disabled={state.disabled}
      />
      <Text
        label={<FormattedMessage {...messages.usernameAttribute} />}
        name="usernameAttribute"
        placeholder={intl.formatMessage(messages.usernameAttributePlaceholder)}
        required
        disabled={state.disabled}
      />
      <Text
        label={<FormattedMessage {...messages.familyNameAttribute} />}
        name="familyNameAttribute"
        placeholder={intl.formatMessage(messages.familyNameAttributePlaceholder)}
        required
        disabled={state.disabled}
      />
    </Form>
  )
}

CreateSamlIdentityProvider.displayName = 'CreateSamlIdentityProvider'

CreateSamlIdentityProvider.propTypes = {
  isActionInProgress: PropTypes.bool.isRequired,
  onCreate: PropTypes.func.isRequired
}

export default CreateSamlIdentityProvider
